import React, { useEffect } from 'react';
import { CheckCircle2, ArrowRight, FileText, Home, Link2, Receipt, PiggyBank } from 'lucide-react';
import { Button } from '../ui/button';
import confetti from 'canvas-confetti';

export default function SuccessStep({ onGoToDashboard, accountingMode }) {
  const isNativeMode = accountingMode === 'native';

  useEffect(() => {
    // Trigger confetti on mount
    const duration = 2000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.8 },
        colors: ['#2563eb', '#16a34a', '#f59e0b'],
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.8 },
        colors: ['#2563eb', '#16a34a', '#f59e0b'],
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, []);

  return (
    <div className="text-center py-8 space-y-8">
      <div className="relative">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-14 h-14 text-green-600" />
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center animate-bounce">
          <span className="text-lg">!</span>
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          Incorporacion completada!
        </h2>
        <p className="text-slate-600 max-w-md mx-auto">
          Tu cuenta esta configurada y lista para empezar a gestionar tus servicios administrativos.
        </p>
      </div>

      {/* Accounting Mode Badge */}
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
        isNativeMode ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
      }`}>
        {isNativeMode ? (
          <>
            <Home className="w-4 h-4" />
            <span className="font-medium">Modo: Contabilidad Nativa Zentro</span>
          </>
        ) : (
          <>
            <Link2 className="w-4 h-4" />
            <span className="font-medium">Modo: Integracion con Holded</span>
          </>
        )}
      </div>

      <div className="bg-slate-50 rounded-xl p-6 max-w-md mx-auto text-left space-y-4">
        <h3 className="font-semibold text-slate-900">Que sigue ahora?</h3>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold">1</span>
            </div>
            <div>
              <div className="font-medium text-slate-900">Nuestro equipo revisara tus datos</div>
              <p className="text-sm text-slate-600">
                Verificaremos la informacion y activaremos tu cuenta en 24-48h
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold">2</span>
            </div>
            <div>
              <div className="font-medium text-slate-900">Recibiras confirmacion por email</div>
              <p className="text-sm text-slate-600">
                Te notificaremos cuando tu cuenta este lista para operar
              </p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-xs font-bold">3</span>
            </div>
            <div>
              <div className="font-medium text-slate-900">
                {isNativeMode ? 'Empieza a facturar' : 'Crea tu primera solicitud'}
              </div>
              <p className="text-sm text-slate-600">
                {isNativeMode
                  ? 'Accede a los modulos de facturacion y gastos desde tu panel'
                  : 'Podras empezar a enviar solicitudes de facturacion y mas'
                }
              </p>
            </div>
          </li>
        </ul>
      </div>

      {/* Native Mode Features Highlight */}
      {isNativeMode && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 max-w-md mx-auto">
          <h4 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
            <Home className="w-5 h-5" />
            Modulos Disponibles
          </h4>
          <div className="grid grid-cols-2 gap-3 text-left">
            <div className="flex items-center gap-2 text-sm text-green-800">
              <Receipt className="w-4 h-4" />
              <span>Facturacion</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-green-800">
              <PiggyBank className="w-4 h-4" />
              <span>Gastos</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-green-800">
              <FileText className="w-4 h-4" />
              <span>PDFs automaticos</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-green-800">
              <ArrowRight className="w-4 h-4" />
              <span>Exportar datos</span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
        {isNativeMode ? (
          <>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/client/invoices'}
              className="gap-2"
            >
              <Receipt className="w-4 h-4" />
              Ir a Facturacion
            </Button>
            <Button
              onClick={onGoToDashboard}
              className="gap-2"
              data-testid="btn-go-dashboard"
            >
              Ir al Panel
              <ArrowRight className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/client/requests'}
              className="gap-2"
            >
              <FileText className="w-4 h-4" />
              Crear Primera Solicitud
            </Button>
            <Button
              onClick={onGoToDashboard}
              className="gap-2"
              data-testid="btn-go-dashboard"
            >
              Ir al Panel
              <ArrowRight className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Tienes dudas? Escribenos a{' '}
        <a href="mailto:soporte@zentro.com.es" className="text-primary hover:underline">
          soporte@zentro.com.es
        </a>
      </p>
    </div>
  );
}

